import { useNavigate, useParams } from "react-router-dom";
import { getContractApi } from "src/api";
import { useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import type { ContractAnalysisMode, GetContractResponseContent } from "src/types/contract.types";
import useSWR from "swr";
import { PATH_ROOT } from "src/routes/paths";

// With strongly typed API models, we can simplify the validation
export const useContractAnalysis = (providedContract?: GetContractResponseContent, isDemo: boolean = false) => {
  const [activeKey, setActiveKey] = useState<ContractAnalysisMode>("eq");
  const navigate = useNavigate();
  const [contract, setContract] = useState<GetContractResponseContent | null>(providedContract || null);
  const [searchParams] = useSearchParams();
  const { contractId: pathContractId } = useParams<{ contractId: string }>();

  // Check path params first, then query params
  const contractId = pathContractId || searchParams.get("id") || "";

  // Define the fetcher function that SWR will use
  const contractFetcher = async () => {
    // If providedContract exists, don't fetch - we'll use it directly
    if (providedContract) return null;
    if (!contractId) return null;

    return getContractApi(contractId);
  };

  // Use a stable key that depends on contractId
  const swrKey = contractId ? ['contract', contractId] : null;
  
  const { data, isLoading, error, mutate } = useSWR(
    swrKey,
    contractFetcher,
    {
      onSuccess: (data) => {
        if (!data) {
          console.warn("Data was successfully fetched but is empty");
        } else if (!data.contractId) {
          console.warn("Data exists but contractId is missing:", data);
        }
      },
      onError: (err) => {
        console.error("Error fetching contract data:", err);
      }
    }
  );

  useEffect(() => {
    // If we have a providedContract, use it and skip fetching
    if (providedContract) {
      setContract(providedContract);
      return;
    }

    if (data) {
      // V1 API: Use data directly without adaptation
      setContract(data);
    }
  }, [data, providedContract]); // Run when data or providedContract changes

  const handleBackClick = () => {
    navigate(isDemo ? PATH_ROOT.demo.vault : PATH_ROOT.main.vault);
  };

  const refreshContract = async () => {
    try {
      // Trigger a revalidation with SWR
      await mutate();
    } catch (error) {
      console.error("Error refreshing contract:", error);
    }
  };
  

  return {
    activeKey,
    setActiveKey,
    contract,
    handleBackClick,
    isLoading,
    error,
    refreshContract,
  };
};
